import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useSelector } from '@tanstack/react-store'
import { toast } from 'sonner'
import { Table, Compass, Users, Trash2, Plus, Check, Loader2, Code, Copy, ChartLine, LayoutDashboard, Stethoscope } from 'lucide-react'
import { copyToClipboard } from '../../utils/clipboard'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { AdvancedSection } from '../../components/AdvancedSection'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AuthService } from '@/services/core/auth'
import { AdUnitsApi, AdUnitInput, AdUnit } from '@/services/adUnits/adUnitsApi'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { ListTabs } from '../../components/ListTabs'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { StatsPanel } from '../../components/StatsPanel'
import { LabelAutocomplete } from '../../components/LabelAutocomplete'
import { FieldLabel } from '../../components/FieldLabel'
import { StatsBar, StatsBarId, StatsBarTraffic, StatsBarActivity } from '../../components/StatsBar'
import { RouteTabs } from '../../components/RouteTabs'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { SitesApi } from '@/services/sites/sitesApi'
import { EarningsAccountsApi } from '@/services/earnings/earningsAccountsApi'
import { useNetwork } from '../../contexts/NetworkContext'
import { adTagTemplates, vastVersions } from '../../data/adTagTemplates'
import {
  findAdTagTemplate,
  renderAdTag,
  renderVastTag,
  type RefreshEvent,
} from '@/services/adUnits/adUnitTags'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { AdUnitAdRequestDiagnostics } from '@/components/diagnostics/AdUnitAdRequestDiagnostics'
import { useCurrentUser } from '@/contexts/UserContext'
import { isSuperAdmin } from '@/data/superAdmins'

/** Sentinel id for the VAST entry, which is a URL rather than a tag template. */
const VAST_TAG_TYPE = 'vast'

// ── Inner form component — only mounts once adUnit is ready ──────────────────

interface AdUnitEditFormProps {
  adUnit: AdUnit | null
  id: string
  isNew: boolean
  initialSiteId: string
}

function AdUnitEditForm({ adUnit, id, isNew, initialSiteId }: AdUnitEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()
  const hasToken = AuthService.isAuthenticated()
  // The overview holds the form, so it is only ever hidden, never unmounted;
  // diagnostics, like the diagnostics page, is for super admins only.
  const [pageTab, setPageTab] = useState<'overview' | 'diagnostics'>('overview')
  const showDiagnostics = !!adUnit && !isNew && isSuperAdmin(currentUser?.username)
  const { data: sites = [] } = useQuery({
    queryKey: ['sites'],
    queryFn: () => SitesApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const { data: earningsAccounts = [] } = useQuery({
    queryKey: ['earningsAccounts'],
    queryFn: () => EarningsAccountsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })

  const SIZE_MODE_OPTIONS = [
    { label: t('adUnits.specificSizes'), value: 'dimensions' },
    { label: t('adUnits.minMaxRange'), value: 'range' },
  ]

  const hasNonZeroRange = !!(adUnit?.minWidth || adUnit?.width || adUnit?.minHeight || adUnit?.height)
  const hasDimensions = adUnit?.dimensions && adUnit.dimensions.length > 0
  const [sizeMode, setSizeMode] = useState<'dimensions' | 'range'>(
    hasNonZeroRange && !hasDimensions ? 'range' : 'dimensions'
  )
  // VAST is not an adn.js template but a URL for a video player, so it sits
  // alongside the templates in the dropdown and renders its own way.
  const [selectedTagType, setSelectedTagType] = useState(adTagTemplates[0].id)
  const [vastVersion, setVastVersion] = useState('vast4')
  // Holding the request back until the unit nears the viewport is a property of
  // the request, so it applies to any of the adn.js tag types.
  const [lazyRequest, setLazyRequest] = useState(false)
  const [proximity, setProximity] = useState(0)
  // Re-requesting the same ad after it has been seen: how long after, how many
  // times, and which event starts the clock.
  const [tagAdvancedOpen, setTagAdvancedOpen] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [refreshDelay, setRefreshDelay] = useState(30)
  const [refreshCount, setRefreshCount] = useState(1)
  const [refreshEvent, setRefreshEvent] = useState<RefreshEvent>('onViewable')
  const tagTypes = [
    ...adTagTemplates.map((tpl) => ({ id: tpl.id, label: tpl.label, description: tpl.description })),
    { id: VAST_TAG_TYPE, label: t('adUnits.tagTypeVast'), description: t('adUnits.tagTypeVastHelp') },
  ]
  const selectedType = tagTypes.find((type) => type.id === selectedTagType)
  const tagAdUnit = { tagId: adUnit?.tagId ?? id, name: adUnit?.name ?? '', site: adUnit?.site }
  const tagOptions = {
    networkTagId: currentNetwork?.tagId ?? '',
    gdpr: 'UNSET' as const,
    ...(lazyRequest ? { lazyRequest: { proximity } } : {}),
    ...(autoRefresh
      ? { refresh: { delay: refreshDelay, count: refreshCount, event: refreshEvent } }
      : {}),
  }
  const adTagCode =
    selectedTagType === VAST_TAG_TYPE
      ? renderVastTag(tagAdUnit, vastVersion, tagOptions)
      : renderAdTag(findAdTagTemplate(selectedTagType), [tagAdUnit], tagOptions)

  const form = useForm({
    defaultValues: {
      name: adUnit?.name ?? '',
      siteId: adUnit?.site?.id ?? initialSiteId,
      labels: adUnit?.labels ?? [],
      matchingLabels: adUnit?.matchingLabels ?? [],
      dimensions: adUnit?.dimensions ?? ([] as [number, number][]),
      minWidth: adUnit?.minWidth ?? null as number | null,
      width: adUnit?.width ?? null as number | null,
      minHeight: adUnit?.minHeight ?? null as number | null,
      height: adUnit?.height ?? null as number | null,
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        objectState: (adUnit?.objectState ?? 'ACTIVE') as ObjectState,
        labels: value.labels,
        matchingLabels: value.matchingLabels,
        ...(value.siteId && { site: { id: value.siteId } }),
        dimensions: sizeMode === 'dimensions' ? value.dimensions : [],
        minWidth: sizeMode === 'range' ? value.minWidth : 0,
        width: sizeMode === 'range' ? value.width : 0,
        minHeight: sizeMode === 'range' ? value.minHeight : 0,
        height: sizeMode === 'range' ? value.height : 0,
      })
    },
  })

  const formSiteId = useSelector(form.store, (s) => s.values.siteId)
  const saveObjectState = useSaveObjectState(
    (objectState) => AdUnitsApi.update(id, { name: adUnit?.name ?? '', objectState }),
    ['adUnit', id],
    ['adUnits']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const parentSiteId = formSiteId || adUnit?.site?.id || initialSiteId
  const parentSite = sites.find(s => s.id === parentSiteId)
  const parentPublisher = earningsAccounts.find(ea => ea.id === parentSite?.earningsAccountId)
  const statsQuery = useMemo(() => ({ adUnitId: id, groupBy: 'HALF_HOURLY' as const, includeChunkRange: true }), [id])

  const mutation = useMutation({
    mutationFn: (input: AdUnitInput) =>
      isNew ? AdUnitsApi.create(id, input) : AdUnitsApi.update(id, input),
    onSuccess: (data, input) => {
      const existing = queryClient.getQueryData<AdUnit>(['adUnit', id])
      const submittedSiteId = (input.site as { id: string } | undefined)?.id ?? existing?.site?.id ?? ''
      const cachedSites = queryClient.getQueryData<{ id: string; name: string }[]>(['sites']) ?? []
      const siteName = existing?.site?.name || cachedSites.find((s) => s.id === submittedSiteId)?.name || ''
      const merged: AdUnit = {
        ...(existing ?? {}),
        ...data,
        site: data.site?.id ? data.site : (submittedSiteId ? { id: submittedSiteId, name: siteName } : null),
      }
      queryClient.setQueryData(['adUnit', id], merged)
      queryClient.setQueryData(['adUnits'], (old: AdUnit[] | undefined) => {
        if (!old) return [merged]
        const idx = old.findIndex((a) => a.id === merged.id)
        return idx >= 0 ? old.map((a) => (a.id === merged.id ? merged : a)) : [...old, merged]
      })
      markClean()
      if (isNew) navigate(`/publishing/ad-units/${id}`, { replace: !createDialog })
      toast.success(t('common.saved'), { description: isNew ? t('adUnits.createdSuccess') : t('adUnits.savedSuccess'), duration: 3000 })
    },
    onError: (error) => {
      const summary = error instanceof ApiError ? error.message : isNew ? t('adUnits.createFailed') : t('adUnits.saveFailed')
      const detail = apiErrorDescription(error)
      toast.error(summary, { description: detail, duration: 5000 })
    },
  })

  const clearRange = () => {
    form.setFieldValue('minWidth', 0)
    form.setFieldValue('width', 0)
    form.setFieldValue('minHeight', 0)
    form.setFieldValue('height', 0)
  }

  const clearDimensions = () => {
    form.setFieldValue('dimensions', [])
  }

  const crumbs = [
    ...(parentPublisher
      ? [{ label: parentPublisher.name, href: `/publishing/earnings/${parentPublisher.id}` }]
      : [{ label: t('publishers.title'), href: '/publishing/earnings' }]),
    ...(parentSite
      ? [{ label: parentSite.name, href: `/publishing/sites/${parentSite.id}` }]
      : []),
    { label: isNew ? t('adUnits.newTitle') : (adUnit?.name ?? t('adUnits.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack objectType={isNew ? undefined : t('objectTypes.adUnit')}>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={adUnit?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.adUnit')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('adUnits.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {adUnit && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={adUnit!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {adUnit && !isNew && (
          <StatsBar
            items={[
              {
                label: t('adUnits.traffic'),
                content: <StatsBarTraffic active={adUnit.requestedInLastDay} />,
              },
              {
                label: t('adUnits.tagId'),
                content: <StatsBarId value={adUnit.tagId ?? id} always />,
              },
              {
                label: t('adUnits.site'),
                content: (
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ad-text)' }}>
                    {parentSite?.name ?? '—'}
                  </span>
                ),
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(adUnit.createTime)}
                    updated={formatDate(adUnit.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        {showDiagnostics && (
          <ListTabs
            variant="line"
            tabs={[
              {
                key: 'overview',
                label: t('adUnits.pageTabs.overview'),
                icon: <LayoutDashboard />,
              },
              {
                key: 'diagnostics',
                label: t('adUnits.pageTabs.diagnostics'),
                icon: <Stethoscope />,
              },
            ]}
            value={pageTab}
            onValueChange={setPageTab}
          />
        )}

        <div className={`ad-edit-columns${pageTab === 'overview' ? '' : ' hidden!'}`}>
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Table} />
                  {t('adUnits.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('publishers.labels')}</label>
                    <form.Field name="labels">
                      {(field) => (
                        <LabelAutocomplete
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <FieldLabel tooltip={t('adUnits.matchingLabelsHelp')}>
                      {t('adUnits.matchingLabels')}
                    </FieldLabel>
                    <form.Field name="matchingLabels">
                      {(field) => (
                        <LabelAutocomplete
                          types="MatchingLabel"
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v)}
                          placeholder={t('adUnits.matchingLabelsPlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <label>{t('adUnits.sizeMode')}</label>
                    <ListTabs
                      className="self-start"
                      tabs={SIZE_MODE_OPTIONS.map((opt) => ({
                        key: opt.value as 'dimensions' | 'range',
                        label: opt.label,
                      }))}
                      value={sizeMode}
                      onValueChange={setSizeMode}
                    />
                  </div>

                  {sizeMode === 'dimensions' && (
                    <div className="p-field full">
                      <label>{t('adUnits.dimensions')}</label>
                      <form.Field name="dimensions">
                        {(field) => (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {field.state.value.map(([w, h], i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <NumberInput
                                  value={w}
                                  onChange={(v) => {
                                    const next = [...field.state.value] as [number, number][]
                                    next[i] = [v ?? 0, next[i][1]]
                                    field.handleChange(next)
                                    clearRange()
                                  }}
                                  min={0}
                                  placeholder={t('adUnits.widthPlaceholder')}
                                  style={{ flex: 1 }}
                                />
                                <span style={{ color: 'var(--ad-text-muted)', fontSize: 13 }}>&times;</span>
                                <NumberInput
                                  value={h}
                                  onChange={(v) => {
                                    const next = [...field.state.value] as [number, number][]
                                    next[i] = [next[i][0], v ?? 0]
                                    field.handleChange(next)
                                    clearRange()
                                  }}
                                  min={0}
                                  placeholder={t('adUnits.heightPlaceholder')}
                                  style={{ flex: 1 }}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => field.handleChange(field.state.value.filter((_, j) => j !== i))}
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => { field.handleChange([...field.state.value, [0, 0]]); clearRange() }}
                              style={{ alignSelf: 'flex-start' }}
                            >
                              <Plus className="size-4" />
                              {t('adUnits.addSize')}
                            </Button>
                          </div>
                        )}
                      </form.Field>
                    </div>
                  )}

                  {sizeMode === 'range' && (
                    <div className="p-field full">
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--ad-text-muted)' }}>{t('adUnits.minWidth')}</label>
                          <form.Field name="minWidth">
                            {(field) => (
                              <NumberInput value={field.state.value} onChange={(v) => { field.handleChange(v ?? null); clearDimensions() }} min={0} placeholder="—" />
                            )}
                          </form.Field>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--ad-text-muted)' }}>{t('adUnits.maxWidth')}</label>
                          <form.Field name="width">
                            {(field) => (
                              <NumberInput value={field.state.value} onChange={(v) => { field.handleChange(v ?? null); clearDimensions() }} min={0} placeholder="—" />
                            )}
                          </form.Field>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--ad-text-muted)' }}>{t('adUnits.minHeight')}</label>
                          <form.Field name="minHeight">
                            {(field) => (
                              <NumberInput value={field.state.value} onChange={(v) => { field.handleChange(v ?? null); clearDimensions() }} min={0} placeholder="—" />
                            )}
                          </form.Field>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--ad-text-muted)' }}>{t('adUnits.maxHeight')}</label>
                          <form.Field name="height">
                            {(field) => (
                              <NumberInput value={field.state.value} onChange={(v) => { field.handleChange(v ?? null); clearDimensions() }} min={0} placeholder="—" />
                            )}
                          </form.Field>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Site */}
            <Card tone="delivery">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Compass} />
                  {t('adUnits.site')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('adUnits.site')}</label>
                    <form.Field name="siteId">
                      {(field) => (
                        <Select label={t('adUnits.site')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('adUnits.selectSite')}>
                              {field.state.value ? (sites.find((s) => s.id === field.state.value)?.name ?? field.state.value) : null}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__clear__">—</SelectItem>
                            {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Teams */}
            {adUnit && !isNew && adUnit.teams.length > 0 && (
              <Card tone="people">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Users} />
                    {t('common.teams')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {adUnit.teams.map((team) => (
                      <span key={team.id} className="chip chip--running" style={{ fontSize: 11 }}>
                        {team.name}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column: tabbed panel */}
          {!isNew && (
            <div className="ad-panel">
              <RouteTabs
                tabs={[
                  {
                    key: 'statistics',
                    label: t('adUnits.statistics'),
                    icon: <ChartLine />,
                    content: () => <StatsPanel query={statsQuery} storageKey="adUnit" />,
                  },
                  {
                    key: 'tag',
                    label: t('adUnits.tagTab'),
                    icon: <Code />,
                    content: () => (
                      <Card>
                        <CardHeader>
                          <CardTitle>
                            <CardIcon icon={Code} />
                            {t('adUnits.adTag')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p style={{ fontSize: 13, color: 'var(--ad-text-muted)', marginBottom: 12 }}>
                            {t('adUnits.adTagDescription')}
                          </p>
                          <div style={{ marginBottom: 12 }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ad-text-muted)', display: 'block', marginBottom: 4 }}>
                              {t('adUnits.tagType')}
                            </label>
                            <Select label={t('adUnits.tagType')} value={selectedTagType} onValueChange={(v) => setSelectedTagType(v ?? '')}>
                              <SelectTrigger style={{ maxWidth: 300 }}><SelectValue placeholder={t('adUnits.tagType')}>{selectedType?.label ?? null}</SelectValue></SelectTrigger>
                              <SelectContent>
                                {tagTypes.map((type) => <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <p style={{ fontSize: 12, color: 'var(--ad-text-faint)', marginTop: 4 }}>
                              {selectedType?.description}
                            </p>
                          </div>
                          {selectedTagType !== VAST_TAG_TYPE && (
                            <AdvancedSection
                              open={tagAdvancedOpen}
                              onOpenChange={setTagAdvancedOpen}
                              activeCount={(lazyRequest ? 1 : 0) + (autoRefresh ? 1 : 0)}
                              className="mb-3"
                            >
                              <div style={{ marginBottom: 12 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                  <Checkbox
                                    checked={lazyRequest}
                                    onCheckedChange={(value) => setLazyRequest(!!value)}
                                  />
                                  {t('adUnits.lazyRequest')}
                                </label>
                                <p style={{ fontSize: 12, color: 'var(--ad-text-faint)', marginTop: 4 }}>
                                  {t('adUnits.lazyRequestHelp')}
                                </p>
                                {lazyRequest && (
                                  <div style={{ marginTop: 8 }}>
                                    <label style={{ fontSize: 12, color: 'var(--ad-text-muted)', display: 'block', marginBottom: 4 }}>
                                      {t('adUnits.lazyRequestProximity')}
                                    </label>
                                    <NumberInput
                                      min={0}
                                      value={proximity}
                                      onChange={(value) => setProximity(Math.max(0, value ?? 0))}
                                      style={{ maxWidth: 110 }}
                                    />
                                  </div>
                                )}
                              </div>
                              <div style={{ marginBottom: 12 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                  <Checkbox
                                    checked={autoRefresh}
                                    onCheckedChange={(value) => setAutoRefresh(!!value)}
                                  />
                                  {t('adUnits.autoRefresh')}
                                </label>
                                <p style={{ fontSize: 12, color: 'var(--ad-text-faint)', marginTop: 4 }}>
                                  {t('adUnits.autoRefreshHelp')}
                                </p>
                                {autoRefresh && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
                                    <label style={{ fontSize: 12, color: 'var(--ad-text-muted)' }}>
                                      {t('adUnits.autoRefreshDelay')}
                                    </label>
                                    <NumberInput
                                      min={0}
                                      value={refreshDelay}
                                      onChange={(value) => setRefreshDelay(Math.max(0, value ?? 0))}
                                      style={{ maxWidth: 90 }}
                                    />
                                    <label style={{ fontSize: 12, color: 'var(--ad-text-muted)' }}>
                                      {t('adUnits.autoRefreshCount')}
                                    </label>
                                    <NumberInput
                                      min={1}
                                      value={refreshCount}
                                      onChange={(value) => setRefreshCount(Math.max(1, value ?? 1))}
                                      style={{ maxWidth: 90 }}
                                    />
                                    <Select
                                      value={refreshEvent}
                                      onValueChange={(v) => setRefreshEvent(v as RefreshEvent)}
                                    >
                                      <SelectTrigger size="sm" style={{ maxWidth: 200 }}>
                                        <SelectValue>{t(`adUnits.refreshEvents.${refreshEvent}`)}</SelectValue>
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="onViewable">
                                          {t('adUnits.refreshEvents.onViewable')}
                                        </SelectItem>
                                        <SelectItem value="onVisible">
                                          {t('adUnits.refreshEvents.onVisible')}
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                )}
                              </div>
                            </AdvancedSection>
                          )}
                          {selectedTagType === VAST_TAG_TYPE && (
                            <div style={{ marginBottom: 12 }}>
                              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ad-text-muted)', display: 'block', marginBottom: 4 }}>
                                {t('adUnitTags.vastVersion')}
                              </label>
                              <Select label={t('adUnitTags.vastVersion')} value={vastVersion} onValueChange={(v) => setVastVersion(v ?? '')}>
                                <SelectTrigger style={{ maxWidth: 300 }}><SelectValue>{vastVersions.find((v) => v.id === vastVersion)?.label ?? null}</SelectValue></SelectTrigger>
                                <SelectContent>
                                  {vastVersions.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <pre
                            style={{
                              background: 'var(--ad-neutral-soft)',
                              border: '1px solid var(--ad-border)',
                              borderRadius: 'var(--ad-radius-sm)',
                              padding: 16,
                              fontSize: 12,
                              lineHeight: 1.6,
                              overflowX: 'auto',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                            }}
                          >
                            {adTagCode}
                          </pre>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            style={{ marginTop: 12 }}
                            onClick={() => copyToClipboard(adTagCode)}
                          >
                            <Copy className="size-4" />
                            {t('common.copyToClipboard')}
                          </Button>
                        </CardContent>
                      </Card>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </div>

        {showDiagnostics && pageTab === 'diagnostics' && (
          <AdUnitAdRequestDiagnostics adUnit={adUnit} />
        )}

        {/* Save bar */}
        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button type="button" variant="ghost" onClick={() => cancelNew('/publishing/ad-units')} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('adUnits.createAdUnit') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page — fetches data, shows loading/error, then mounts form ─────────

export function AdUnitEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const initialSiteId = isNew ? (searchParams.get('site') ?? '') : ''
  const hasToken = AuthService.isAuthenticated()

  const { data: adUnit, isLoading, isError } = useQuery({
    queryKey: ['adUnit', id],
    queryFn: () => AdUnitsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('publishers.title'), href: '/publishing/earnings' },
    { label: isNew ? t('adUnits.newTitle') : t('adUnits.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  // For existing ad units: adUnit is guaranteed loaded here.
  // For new ad units: adUnit is null (query disabled), render immediately.
  return (
    <AdUnitEditForm
      key={id}
      adUnit={adUnit ?? null}
      id={id!}
      isNew={isNew}
      initialSiteId={initialSiteId}
    />
  )
}
